import * as _ from 'underscore';

import { call, put, select } from 'redux-saga/effects';
import { Messages } from '../../../../codegen/Localize';
import { ProjectDefinition } from '../../../../ts/models/ContentDefinitions';
import { IContentInfo } from '../../../../ts/models/IContentInfo';
import { isIProjectBase } from '../../../../ts/models/IProjectInfo';
import { IViewInfo } from '../../../../ts/models/IViewInfo';
import { isIProjectItemInfo, ISpaceTypeInfo } from '../../../../ts/models/resourceInfoParts';
import { Set } from '../../../../ts/models/Set';
import { ExploreContentApi_ContentType } from '../../../../ts/ServerApi/types/IContent';
import { ContentTypeAndID, DeleteContentsAction, IDeleteContentsResult } from '../../../../ts/services/DeleteContentsAction';
import { HideViewsAction } from '../../../../ts/services/HideViewsAction';
import { ToasterService } from '../../../../ts/services/ToasterService';
import { requestFailed } from '../../../actions/RequestFailedAction';
import { removeFromContentSelection } from '../../../actions/spaces/ContentExplorerSelectionActions';
import { DeleteCollectionAction } from '../../../actions/spaces/DeleteCollectionAction';
import { DeleteContentAction, deleteContentSucceeded } from '../../../actions/spaces/DeleteContentAction';
import { requestSpaceContents } from '../../../actions/spaces/RequestSpaceContentsActions';
import { siteUrlsSelector } from '../../../selectors/SiteUrlsSelectors';
import { selectSpaceInfo } from '../../../selectors/SpaceInfoSelectors';
import { ServiceLocator } from '../../../ServiceLocator';
import { DeleteCollectionDialog, DeleteCollectionDialogCloseReason } from '../../../spaces/Collections/DeleteCollectionDialog';
import { SpacesSiteUrls } from '../../../spaces/SpacesSiteUrls';
import { SpaceInfo } from '../../../state/SpaceInfo';
import { replaceDatasourceIdWithConnectionIdForMixedContent } from '../SpacesSagaHelpers';

export function openConfirmationDialog(contents: IContentInfo[]) {
  const deleteContentActionService = ServiceLocator.get<DeleteContentsAction>('DeleteContentsAction');
  const context = ServiceLocator.getAngularActionContext();
  return deleteContentActionService.confirmDeleteContents(contents, context);
}

export function* deleteContents(action: DeleteContentAction) {
  try {
    let mutatableContentCopy: IContentInfo[] = [];
    mutatableContentCopy = yield call(replaceDatasourceIdWithConnectionIdForMixedContent, action.contentArray);

    const result: IDeleteContentsResult = yield call(openConfirmationDialog, mutatableContentCopy);
    yield put(removeFromContentSelection(...action.contentArray));

    const currentSpaceInfo: SpaceInfo = yield select(selectSpaceInfo);

    // if we've deleted the space whose page we are currently viewing
    // navigate back to the parent project
    const isCurrentSpace = (item: IContentInfo) => {
      return (item.contentType === currentSpaceInfo.spaceType
            && item.id === currentSpaceInfo.item.id);
    };

    let workbookForSpaceAlsoDeleted: boolean =
      allViewsInWorkbookDeletedForCurrentWorkbookSpace(action.contentArray, result, currentSpaceInfo);

    const deletedItemIfInCurrentSpace: IContentInfo = _.find(action.contentArray, isCurrentSpace);

    if (!!deletedItemIfInCurrentSpace || !!workbookForSpaceAlsoDeleted) {
      const parentProjectId: string = workbookForSpaceAlsoDeleted?
        getParentProjectId(currentSpaceInfo.item) :
        getParentProjectId(deletedItemIfInCurrentSpace);

      const urls: SpacesSiteUrls = yield select(siteUrlsSelector);

      // keep these success toasts when navigating away to make sure the user knows what happened
      const toaster: ToasterService = ServiceLocator.get<ToasterService>('ToasterService');
      toaster.markToastsAsDoNotDismissOnLocationChange();

      const locationService = ServiceLocator.get<ng.ILocationService>('$location');
      if (parentProjectId) {
        locationService.url(urls.projectPath(parentProjectId));
      } else {
        locationService.url(urls.explorePath());
      }
      // when updating the url outside Angular, $apply is needed to update the window location.
      ServiceLocator.getAngularActionContext().scope.$apply();
    } else {
      // refetch content for this space.
      yield put(requestSpaceContents());
      yield put(deleteContentSucceeded());
    }
  } catch (e) {
    yield put(requestFailed(e, Messages.deleteFailed()));
  }
}

// exported for testing
export function allViewsInWorkbookDeletedForCurrentWorkbookSpace(
  contentArray: IContentInfo[],
  result: IDeleteContentsResult,
  currentSpaceInfo: SpaceInfo) {

  const isInWorkbookViewsSubSpace = (currentSpaceInfo.spaceType === 'workbook' && currentSpaceInfo.subspaceName === 'views');
  if(!isInWorkbookViewsSubSpace) {
    return false;
  }

  if (contentArray && contentArray.length > 0) {

    const deletedViews = contentArray.filter((item: IContentInfo<ExploreContentApi_ContentType>) => {
      return item.contentType === 'view' && result.deletedItems && arrayContainsViewWithId(result.deletedItems, item.id);
    });
    const workbooksDeleted = HideViewsAction.determineWorkbooksToBeDeleted((Set.fromIdItemArray(deletedViews) as Set<IViewInfo>));
    return workbooksDeleted.length === 1 && workbooksDeleted[0].id === currentSpaceInfo.item.id;
  }
  return false;
}


// exported
export function arrayContainsViewWithId(arr: ContentTypeAndID[], idToMatch: string): boolean {
  return arr.findIndex((item: ContentTypeAndID) => item.contentType === 'view' && item.id  === idToMatch) >= 0;
}

function getParentProjectId(item: IContentInfo | ISpaceTypeInfo) {
  if (item.contentType === ProjectDefinition.contentType && isIProjectBase(item)) {
    if (!item.topLevelProject) {
      return item.parentId;
    }
  }
  else if (isIProjectItemInfo(item)) {
    return item.project.id;
  }

  return undefined;
}

// Delete Collection
export function* deleteCollection(action: DeleteCollectionAction) {
  try {
    const id = action.targetItem.id;
    const name = action.targetItem.name;

    const closeReason = yield call(DeleteCollectionDialog.open, id, name);

    if(_.isEqual(closeReason, DeleteCollectionDialogCloseReason.Delete)) {
      yield put(removeFromContentSelection(action.targetItem));
      yield put(requestSpaceContents());
      yield put(deleteContentSucceeded());
    }
  } catch (e) {
    yield put(requestFailed(e, Messages.deleteFailed()));
  }
}
